import { snackbar } from '../components/services/SnackbarService'
import { IFormField } from '../definitions/FormField'
import { IFormGroup } from '../definitions/FormGroup'

export type ValidationError = {
    message: string
}

export const ContractSchemaManager = {
    setNodeAtPath: (schema: IFormGroup[], path: number[], updatedNode: any): IFormGroup[] => {
        if (path.length === 0) return schema
        const newSchema = [...schema]
        const [stepIndex, ...restPath] = path

        newSchema[stepIndex] = { ...schema[stepIndex] }
        if (restPath.length === 0) {
            return newSchema
        }
        newSchema[stepIndex].fields = [...schema[stepIndex].fields]

        let current = newSchema[stepIndex].fields

        for (let i = 0; i < restPath.length - 1; i++) {
            const index = restPath[i]
            const field = { ...current[index] }
            current[index] = field

            if (field.childFields) {
                field.childFields = [...field.childFields]
                current = field.childFields
            } else if (field.objectFields) {
                field.objectFields = [...field.objectFields]
                current = field.objectFields
            } else {
                break
            }
        }

        current[restPath[restPath.length - 1]] = updatedNode
        return newSchema
    },

    getNodeAtPath: (schema: IFormGroup[], path: number[]): IFormField => {
        let node: IFormField = { fields: schema, key: null, label: null, type: '' }
        let childArrayKey: 'fields' | 'childFields' | 'objectFields' = 'fields'

        for (let i = 0; i < path.length; i++) {
            const index = path[i]
            const currentArray = node[childArrayKey]
            if (!currentArray) return null

            node = currentArray[index]

            if (node && node.fields) {
                childArrayKey = 'fields'
            } else if (node && node.childFields) {
                childArrayKey = 'childFields'
            } else if (node && node.objectFields) {
                childArrayKey = 'objectFields'
            } else {
                childArrayKey = 'fields'
            }
        }

        return node
    },

    removeField: (schema, setSchema, stepIndex, fieldIndex) => {
        const updatedSchema = [...schema]
        updatedSchema[stepIndex].fields.splice(fieldIndex, 1)
        setSchema(updatedSchema)
    },

    generateKeyFromLabel: (label: string) => {
        if (!label || label.length === 0)
            return ''

        const normalized = label
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9_]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '')

        return normalized
            .trim()
    },

    getParentKeyPath(fullKey?: string): string {
        if (!fullKey || fullKey.length === 0)
            return ''

        const lastDotIndex = fullKey.lastIndexOf('.')
        return lastDotIndex > 0 ? fullKey.substring(0, lastDotIndex) : ''
    },

    validateSchema: (schemaToValidate: IFormGroup[]) => {
        const errors: ValidationError[] = []

        const validateFields = (fields: IFormField[], parentPath: string = '') => {
            const localKeys = new Set<string>()

            fields.forEach((field, index) => {
                const fieldPath = `${parentPath}[${index}]`
                const fieldlabel = field.label || ''

                if (!field.key.trim()) {
                    errors.push({ message: ` Key is required in step "${fieldlabel}"` })
                    snackbar.show({ severity: 'error', text: ` Key is required in "${fieldlabel}"` })
                } else if (localKeys.has(field.key)) {
                    errors.push({ message: ` Key must be unique in  "${fieldlabel}"` })
                    snackbar.show({ severity: 'error', text: ` Key must be unique in  "${fieldlabel}"` })
                } else {
                    localKeys.add(field.key)
                }

                if (field.childFields) {
                    validateFields(field.childFields, `${fieldPath}.childFields`)
                }
                if (field.objectFields) {
                    validateFields(field.objectFields, `${fieldPath}.objectFields`)
                }
            })
        }

        schemaToValidate.forEach((step, stepIndex) => {
            validateFields(step.fields, `steps[${stepIndex}].fields`)
        })

        return errors.map(error => error.message)
    },

    removeFieldAtPath: (schema: IFormGroup[], path: number[]): IFormGroup[] => {
        if (path.length < 2) return schema

        if (path.length === 2) {
            const [stepIndex, fieldIndex] = path
            const newSchema = [...schema]
            newSchema[stepIndex] = {
                ...schema[stepIndex],
                fields: schema[stepIndex].fields.filter((_, idx) => idx !== fieldIndex)
            }
            return newSchema
        }

        const parentPath = path.slice(0, -1)
        const parent = ContractSchemaManager.getNodeAtPath(schema, parentPath)
        const childIndex = path[path.length - 1]

        if (!parent) return schema

        const updatedParent = { ...parent }

        if (parent.type === 'list' && parent.childFields) {
            updatedParent.childFields = parent.childFields.filter((_, idx) => idx !== childIndex)
        } else if (parent.type === 'object' && parent.objectFields) {
            updatedParent.objectFields = parent.objectFields.filter((_, idx) => idx !== childIndex)
        }

        return ContractSchemaManager.setNodeAtPath(schema, parentPath, updatedParent)
    },

    updateFieldAtPath: (schema: IFormGroup[], path: number[], updates: Partial<IFormField>): IFormGroup[] => {
        const node = ContractSchemaManager.getNodeAtPath(schema, path)
        if (!node) return schema

        const updatedNode = { ...node, ...updates }

        if (updates.label && !node.isDefault && (updates.key || node.key)) {
            const oldKey = node.key || ''
            const newKey = updates.key || updatedNode.key || ''

            if (node.type === 'list' && node.childFields) {
                updatedNode.childFields = ContractSchemaManager.updateChildrenKeys(
                    node.childFields,
                    oldKey,
                    newKey
                )
            } else if (node.type === 'object' && node.objectFields) {
                updatedNode.objectFields = ContractSchemaManager.updateChildrenKeys(
                    node.objectFields,
                    oldKey,
                    newKey
                )
            }
        }

        return ContractSchemaManager.setNodeAtPath(schema, path, updatedNode)
    },

    addChildAtPath: (schema: IFormGroup[], path: number[]): IFormGroup[] => {
        const parent = ContractSchemaManager.getNodeAtPath(schema, path)
        if (!parent) return schema

        const parentKey = parent.key || ''
        const childLabel = 'New Child Field'
        const childKey = ContractSchemaManager.generateKeyFromLabel(childLabel)
        const fullKey = parentKey ? `${parentKey}.${childKey}` : childKey

        const newChild: IFormField = {
            description: '',
            isDefault: false,
            label: childLabel,
            type: 'string',
            key: fullKey,
            validation: {
                isRequired: null
            }
        }

        const updatedParent = { ...parent }

        if (parent.type === 'list') {
            updatedParent.childFields = [...(parent.childFields || []), newChild]
        } else if (parent.type === 'object') {
            updatedParent.objectFields = [...(parent.objectFields || []), newChild]
        }

        return ContractSchemaManager.setNodeAtPath(schema, path, updatedParent)
    },

    updateChildrenKeys: (children: IFormField[], oldParentKey: string, newParentKey: string): IFormField[] => {
        return children.map(child => {
            const childOldKey = child.key || ''
            const childBaseKey = oldParentKey
                ? childOldKey.substring(oldParentKey.length + 1)
                : childOldKey
            const childNewKey = newParentKey
                ? `${newParentKey}.${childBaseKey}`
                : childBaseKey

            return {
                ...child,
                key: childNewKey,
                childFields: child.type === 'list' && child.childFields
                    ? ContractSchemaManager.updateChildrenKeys(child.childFields, childOldKey, childNewKey)
                    : child.childFields,
                objectFields: child.type === 'object' && child.objectFields
                    ? ContractSchemaManager.updateChildrenKeys(child.objectFields, childOldKey, childNewKey)
                    : child.objectFields
            }
        })
    }
}
